<script setup lang="ts">
import type { ProjectSummary } from '~/types/content'

defineProps<{
  projects: ProjectSummary[]
}>()

function onCardMove(event: MouseEvent) {
  const card = event.currentTarget as HTMLElement
  const rect = card.getBoundingClientRect()
  card.style.setProperty('--spot-x', `${event.clientX - rect.left}px`)
  card.style.setProperty('--spot-y', `${event.clientY - rect.top}px`)
}
</script>

<template>
  <section class="py-28">
    <div class="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
      <div class="reveal-on-scroll mb-14 text-center">
        <h2 class="text-3xl font-bold text-[var(--color-text)] sm:text-4xl">
          Featured Work
        </h2>
        <p class="mt-3 text-[var(--color-text-muted)]">
          A selection of projects spanning web, hardware, and AI
        </p>
      </div>

      <div class="grid gap-6 md:grid-cols-3">
        <NuxtLink
          v-for="(project, i) in projects"
          :key="project.slug"
          :to="`/projects/${project.slug}`"
          class="project-card reveal-on-scroll group relative overflow-hidden rounded-xl border border-[var(--color-card-border)] bg-[var(--color-card-bg)] p-6 transition-all duration-500 hover:border-[var(--color-amber)]/20 hover:shadow-[0_0_40px_rgba(250,185,91,0.05)]"
          :style="{ transitionDelay: `${i * 120}ms` }"
          @mousemove="onCardMove"
        >
          <!-- Cursor spotlight -->
          <div class="project-card-spotlight pointer-events-none absolute -inset-px rounded-xl opacity-0 transition-opacity duration-500 group-hover:opacity-100" />

          <div class="relative">
            <p class="mb-3 text-xs font-semibold tracking-widest text-[var(--color-amber)] uppercase">
              {{ project.category }}
            </p>
            <h3 class="mb-3 text-xl font-semibold text-[var(--color-text)] transition-colors duration-300 group-hover:text-[var(--color-amber)]">
              {{ project.title }}
            </h3>
            <p class="mb-5 text-sm leading-relaxed text-[var(--color-text-muted)]">
              {{ project.description }}
            </p>
            <div class="flex flex-wrap gap-2">
              <span
                v-for="tag in project.tags"
                :key="tag"
                class="rounded-md border border-[var(--color-card-border)] bg-[var(--color-surface-hover)] px-2.5 py-1 text-xs text-[var(--color-text-muted)] transition-colors group-hover:border-[var(--color-amber)]/10 group-hover:text-[var(--color-text)]"
              >
                {{ tag }}
              </span>
            </div>
          </div>
        </NuxtLink>
      </div>

      <div class="reveal-on-scroll mt-12 text-center">
        <MagneticWrap>
          <UButton
            to="/projects"
            variant="link"
            class="text-[var(--color-amber)] transition-all hover:tracking-wider"
            trailing-icon="i-lucide-arrow-right"
          >
            View all projects
          </UButton>
        </MagneticWrap>
      </div>
    </div>
  </section>
</template>

<style scoped>
.project-card {
  --spot-x: 50%;
  --spot-y: 50%;
}

.project-card-spotlight {
  background: radial-gradient(
    240px circle at var(--spot-x) var(--spot-y),
    rgba(250, 185, 91, 0.1),
    transparent 70%
  );
}

@media (prefers-reduced-motion: reduce) {
  .project-card-spotlight {
    display: none;
  }
}
</style>
